import React from "react";
import {useTranslation} from "react-i18next";
import {ChangeDateFormat} from "../../../Common/Helpers/ChangeDateFormat";
import {ToCustomDecimal} from "../../../AppSystem/Helpers/ToCustomDecimal";
import {CollapsibleSection} from "../../../Common/Components/Detail/CollapsibleSection";
import {Field, FieldBlock} from "../../../Common/Components/Detail/FieldBlock";
import {DetailTable} from "../../../Common/Components/Detail/DetailTable";
import {FileLink} from "../../../Common/Components/Detail/FileLink";
import {GetUnitPrice} from "../../Helpers/AttestAmounts";

const kr = (value) => `${ToCustomDecimal(value ?? 0)} kr`;

const MultilineText = ({text}) => (
    <>
        {String(text).split(/[\n,]+/).map((line, index) => (
            <React.Fragment key={`line${index}`}>
                {line}
                <br />
            </React.Fragment>
        ))}
    </>
);

/*
Sections of an attest of model "Förrättning": Arvode, Bilersättning, Utlägg and
Bilagor. Each section lists its items in a table so the labels are written once.
*/
export function AttestReimbursementSections({data}) {
    const {t} = useTranslation();

    const fixeds = Array.isArray(data?.fixeds) ? [...data.fixeds] : [];
    fixeds.sort((a, b) => new Date(a.salaryDate) - new Date(b.salaryDate));
    const mileage = data?.mileage && data.mileage.id ? data.mileage : null;
    const expenses = Array.isArray(data?.expenses) ? data.expenses : [];

    // Bilagor: participant lists / programs attached to the expenses, plus the
    // free attachments and comments of the reimbursement itself.
    const additionalInfos = expenses.filter(item =>
        item.additionalInfo?.participantsFileId ||
        item.additionalInfo?.programFileId ||
        item.additionalInfo?.program
    );
    const attachmentFiles = data?.attachments?.fileList ?? [];
    const comments = data?.attachments?.commentList ?? [];
    // The section is always there; its header says whether it holds anything.
    const attachmentsCount = additionalInfos.length + attachmentFiles.length + comments.length;
    const attachmentsSubtitle = () => {
        if (attachmentsCount === 0) return t('attest.noAttachments');
        if (attachmentsCount === 1) return t('attest.attachmentOne');
        return t('attest.attachmentsCount', {count: attachmentsCount});
    };

    const fixedsNet = fixeds.reduce((total, item) => total + (item.net ?? 0), 0);
    const fixedSubtitle = fixeds.length === 1
        ? (fixeds[0].type === 'Fixed'
            ? t('reimbursement.fixed')
            : `${t('modelLabel.hours')} - ${fixeds[0].hours ?? 0}h`)
        : `${fixeds.length} ${t('reimbursement.salary').toLowerCase()}`;

    // Arvode and Bilersättning share the same eight columns, so they share the same
    // widths and line up with each other when both are open.
    const payrollWidths = ['11%', '11%', '14%', '12%', '13%', '13%', '13%', '13%'];
    const amountColumn = (label, strong = false, width = null) =>
        ({label, align: 'right', nowrap: true, strong, width});

    return (
        <>
            {fixeds.length > 0 ?
                <CollapsibleSection
                    badge="A"
                    title={t('reimbursement.salary')}
                    subtitle={fixedSubtitle}
                    amountLabel={`${t('modelLabel.net')}:`}
                    amount={kr(fixedsNet)}
                >
                    <DetailTable
                        columns={[
                            {label: t('modelLabel.type'), width: payrollWidths[0], role: 'title'},
                            {label: t('modelLabel.date'), nowrap: true, width: payrollWidths[1], role: 'date'},
                            amountColumn(t('modelLabel.hours'), false, payrollWidths[2]),
                            amountColumn(t('attest.unitPrice'), false, payrollWidths[3]),
                            amountColumn(t('reimbursement.amount'), false, payrollWidths[4]),
                            amountColumn(t('attest.employmentTaxShort'), false, payrollWidths[5]),
                            amountColumn(t('modelLabel.incomeTax'), false, payrollWidths[6]),
                            {...amountColumn(t('modelLabel.net'), true, payrollWidths[7]), role: 'amount'},
                        ]}
                        rows={fixeds.map(item => {
                            const isFixedType = item.type === 'Fixed';
                            const unitPrice = isFixedType ? null : GetUnitPrice(item.amount, item.hours);
                            return [
                                isFixedType ? t('reimbursement.fixed') : t('reimbursement.movable'),
                                item.salaryDate ? ChangeDateFormat('date', item.salaryDate) : '',
                                isFixedType ? '' : (item.hours ?? 0),
                                unitPrice !== null ? kr(unitPrice) : '',
                                kr(item.amount),
                                kr(item.employmentTax),
                                kr(item.incomeTax),
                                kr(item.net),
                            ];
                        })}
                    />
                </CollapsibleSection>
            : ''}

            {mileage ?
                <CollapsibleSection
                    badge="B"
                    title={t('reimbursement.mileage')}
                    subtitle={`${mileage.from ?? ''} → ${mileage.to ?? ''} - ${mileage.km ?? 0} km`}
                    amountLabel={`${t('modelLabel.net')}:`}
                    amount={kr(mileage.net)}
                >
                    <DetailTable
                        columns={[
                            {label: t('reimbursement.origin'), width: payrollWidths[0], role: 'title'},
                            {label: t('reimbursement.destination'), width: payrollWidths[1]},
                            amountColumn(t('modelLabel.kilometers'), false, payrollWidths[2]),
                            amountColumn(t('attest.unitPrice'), false, payrollWidths[3]),
                            amountColumn(t('reimbursement.amount'), false, payrollWidths[4]),
                            amountColumn(t('attest.employmentTaxShort'), false, payrollWidths[5]),
                            amountColumn(t('modelLabel.incomeTax'), false, payrollWidths[6]),
                            {...amountColumn(t('modelLabel.net'), true, payrollWidths[7]), role: 'amount'},
                        ]}
                        rows={[[
                            mileage.from,
                            mileage.to,
                            mileage.km ? `${mileage.km} km` : '',
                            GetUnitPrice(mileage.amount, mileage.km) !== null
                                ? `${ToCustomDecimal(GetUnitPrice(mileage.amount, mileage.km))} kr/km`
                                : '',
                            kr(mileage.amount),
                            kr(mileage.employmentTax),
                            kr(mileage.incomeTax),
                            kr(mileage.net),
                        ]]}
                    />

                    {Array.isArray(mileage.passengers) && mileage.passengers.length > 0 ?
                        <div className="ds-table-stack">
                            <DetailTable
                                columns={[
                                    {label: t('reimbursement.numberPassengers'), role: 'title'},
                                    amountColumn(t('modelLabel.totalDistance')),
                                    {...amountColumn(t('reimbursement.amount'), true), role: 'amount'},
                                ]}
                                rows={mileage.passengers.map(passenger => [
                                    passenger.quantity,
                                    passenger.km ? `${passenger.km} km` : '',
                                    kr(passenger.amount),
                                ])}
                            />
                        </div>
                    : ''}
                </CollapsibleSection>
            : ''}

            {expenses.length > 0 ?
                <CollapsibleSection
                    badge="U"
                    title={t('reimbursement.expenseTitle')}
                    subtitle={t('attest.expensesCount', {count: expenses.length})}
                    amountLabel={`${t('modelLabel.net')}:`}
                    amount={kr(expenses.reduce((total, item) => total + (item.amount ?? 0), 0))}
                >
                    <DetailTable
                        columns={[
                            {label: t('modelLabel.accounting'), role: 'title'},
                            {label: t('modelLabel.dateReceive'), nowrap: true, role: 'date'},
                            amountColumn(t('modelLabel.vatAmount')),
                            {label: t('reimbursement.receipt'), nowrap: true},
                            {...amountColumn(t('modelLabel.net'), true), role: 'amount'},
                        ]}
                        rows={expenses.map(item => [
                            item.accountingName,
                            ChangeDateFormat('date', item.dateReceive),
                            kr(item.vatAmount),
                            <FileLink fileId={item.fileId} fileName={item.fileExtension} />,
                            kr(item.amount),
                        ])}
                    />
                </CollapsibleSection>
            : ''}

            <CollapsibleSection
                title={t('reimbursement.participantsAndPrograms')}
                subtitle={attachmentsSubtitle()}
                collapsible={attachmentsCount > 0}
            >
                {additionalInfos.map((item, index) => (
                    <FieldBlock key={`additional${index}`}>
                        {item.additionalInfo?.program ?
                            <Field
                                label={isNaN(item.additionalInfo.program)
                                    ? t('reimbursement.program')
                                    : t('reimbursement.numberOfParticipants')}
                                value={<MultilineText text={item.additionalInfo.program} />}
                                wide
                            />
                        : ''}
                        {item.additionalInfo?.participantsFileId ?
                            <Field
                                label={t('attest.participantsList')}
                                value={<FileLink fileId={item.additionalInfo.participantsFileId} fileName={item.additionalFileExtension} />}
                            />
                        : ''}
                        {item.additionalInfo?.programFileId ?
                            <Field
                                label={t('attest.programOrInvitation')}
                                value={<FileLink fileId={item.additionalInfo.programFileId} fileName={item.additionalFileExtension} />}
                            />
                        : ''}
                    </FieldBlock>
                ))}

                {attachmentFiles.length > 0 ?
                    <FieldBlock>
                        {attachmentFiles.map((item, index) => (
                            <Field
                                key={`attachment${index}`}
                                label={t('attest.otherDocuments')}
                                value={<FileLink fileId={item.fileId} fileName={item.fileName} />}
                            />
                        ))}
                    </FieldBlock>
                : ''}

                {comments.length > 0 ?
                    <FieldBlock>
                        {comments.map((item, index) => (
                            <Field
                                key={`comment${index}`}
                                label={t('modelLabel.comment')}
                                value={<MultilineText text={item.comment} />}
                                wide
                            />
                        ))}
                    </FieldBlock>
                : ''}
            </CollapsibleSection>
        </>
    );
}
